import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import {
  PpDataParserService,
  PpHttpConfig,
  PpHttpOptions,
  PpHttpParams,
  PpHttpService,
} from '@penpencil/common';
import moment from 'moment';
import { Subscription } from 'rxjs';
import * as _ from 'underscore';

import { PpButton } from './components/button/button.type';
import { PpModalComponent } from './components/modal/modal.component';
import { PreviewDialogComponent } from './components/preview-dialog/preview-dialog.component';
import { PreviewComponent } from './components/preview/preview.component';
import {
  EDITOR_PROPS,
  setConfigForMetaProps,
  uploadSignedURLJSON,
} from './constants/meta-props';
import {
  MAX_IMAGE_SIZE,
  PEN_PENCIL_FORMS_API_CONFIG,
  SELECT_SEARCH_DEFAULT_DEBOUNCE_WAIT,
} from './forms.constant';
import { PpConditionValidationService } from './services/condition-validation.service';
import { FormsService } from './services/forms.service';
import { PpNotificationService } from './services/notification.service';
import {
  ImageDimension,
  PpForm,
  PpFormField,
  PpHideFormFields,
  PpParamInfo,
  PpValueItem,
} from './types/forms.type';
import {
  PpRelativeValidationDisabled,
  PpRelativeValidationEquals,
  PpRelativeValidationFormula,
  PpRelativeValidationLimit,
  PpRelativeValidationMax,
  PpRelativeValidationMin,
  PpRelativeValidationNotEquals,
  PpRelativeValidationRequired,
} from './types/relative-validation';
import { PpHelper } from './utils/helper';

// Load WIRISplugins.js dynamically
const jsDemoImagesTransform = document.createElement('script');
jsDemoImagesTransform.type = 'text/javascript';
jsDemoImagesTransform.src =
  'https://www.wiris.net/demo/plugins/app/WIRISplugins.js?viewer=image';
// Load generated scripts.
document.head.appendChild(jsDemoImagesTransform);

@Component({
  selector: 'pp-form',
  templateUrl: './forms.component.html',
  styleUrls: ['./forms.component.scss'],
})
// Unsubscribe all subscriptions on ngOnDestroy
// @PpAutoUnsubscribe
export class PpFormComponent implements OnInit, AfterViewChecked, OnDestroy {
  // Form Config
  @Input() formConfig!: PpForm;

  // Stepper Form
  @Input() stepperForm!: boolean;

  // Stepper Form
  @Input() backButton!: PpButton;

  // Before Submit Form value event to parent
  @Output() beforeSubmitFormValue = new EventEmitter<any>(true);

  // Form submitted event to parent
  @Output() formSubmitted = new EventEmitter<any>(true);

  // Back button event to parent
  @Output() backButtonClick = new EventEmitter<any>(true);

  // Next button event to parent
  @Output() nextButtonClick = new EventEmitter<any>(true);

  // Form submitted api response to parent
  @Output() getData = new EventEmitter<any>(true);

  @Output() getExistingFormData = new EventEmitter<any>(true);

  // Underscore package
  public _ = _;

  // FormGroup
  public form!: FormGroup;

  // FormFields
  public formFields!: PpFormField[];
  // FormButton
  public formButton!: PpButton;
  // Error Message
  public errorMsg!: string;
  // Loader
  public isLoad!: boolean;
  // Data soure from api/local
  public dataSource: any;
  // Display no. of columns as per row
  public columnPerRow!: number;
  // Header Name
  public header!: string;
  // Display submit button
  public disableSubmitButton = false;

  // to show loading bar under search box in select
  public selectApiLoading: {
    [key: string]: boolean;
  } = {};

  // to show API error under search box in select
  public selectApiError: {
    [key: string]: string;
  } = {};

  // to keep search disabled until all current values have been loaded
  public selectCurrentValueOptionsLoaded: {
    [key: string]: boolean;
  } = {};

  // page no for select
  pageOffset = 0;

  //Image View Value array for multiple select in image

  public imageSrcForMultipleSelect = [''];
  public configForCkEditer = EDITOR_PROPS;

  // Rich text editor options
  // public quillToolbarOptions = QuillToolbarOptions;

  // Tag keys to create
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  // Subscription
  private formSubscription!: Subscription;
  private selectSubscription: {
    [key: string]: Subscription;
  } = {};
  private autoCompleteSubscription!: Subscription;
  private uploadSubscription!: Subscription;
  private submitFormSubscription!: Subscription;
  private asyncValidateSubscription!: Subscription;

  // for local select search: a backup of all values to show when search text is empty
  private selectAllValuesBackup: {
    [key: string]: PpValueItem[];
  } = {};

  constructor(
    private apiService: PpHttpService,
    private formsService: FormsService,
    private notificationService: PpNotificationService,
    private conditionValidationService: PpConditionValidationService,
    private dataParserService: PpDataParserService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    public dialog: MatDialog,
    @Inject(PEN_PENCIL_FORMS_API_CONFIG)
    private config: PpHttpConfig,
  ) {}

  ngAfterViewChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  ngOnInit() {
    setConfigForMetaProps(this.config);
    // Validate Form Config
    // FormConfigValidator.validateFormConfig(this.formConfig);
    this.onSelectSearchTextChange = _.debounce(
      this.onSelectSearchTextChange,
      this.formConfig.selectSearchDebounceWait ??
        SELECT_SEARCH_DEFAULT_DEBOUNCE_WAIT,
    );
    // Defining form fields
    this.formFields = this.formConfig.formFields;
    // Defining form buttons
    this.formButton = this.formConfig.formButton;
    // Defining Header Name
    this.header = this.formConfig.header!;
    // Defining Field Per Row
    this.columnPerRow = this.formConfig.columnPerRow || 1;
    // Check for form type
    if (this.formConfig.type === 'create') {
      // Configure Form
      this.form = this.configureForm(this.formFields);
      // Check values for select field and get in case of api
      this.checkValuesForSelectFields(this.formFields);
      // Set value for autocomplete
      this.setDefaultForAutocomplete(this.formFields);
    } else {
      // Process Form Data In case of edit form
      this.processFormData().then((res) => {
        // Add select all option to all values arrays of type multi-checkbox
        this.formFields.forEach((formField: PpFormField) => {
          // if(formField.type==='multicheckbox' && formField.values && formField.values.length) {
          //   formField.values.unshift({value: 'selectAll', viewValue: 'Select All'});
          // }
          // Setting value to form
          // if(formField.type==='image') {
          //   formField['suffixIcon'] = {
          //   type: 1,
          //   name: 'image',
          //   tooltip: 'add Image',
          //   color: 'warn'
          //   }
          //   formField['outline'] = true
          //  // formField.file.viewValue.value = formField.file.src?.srcForImage;
          // }
          // if(formField.type==='document') {
          //   formField['suffixIcon'] = {
          //     type: 1,
          //     name: 'insert_drive_file',
          //     tooltip: 'add File(s)',
          //     color: 'warn'
          //   }
          //   formField['outline'] = true
          // }
          this.setDefaultValue(formField);
        });
        // Configure Form
        this.form = this.configureForm(this.formFields);
        // Check values for select field and get in case of api
        this.checkValuesForSelectFields(this.formFields);
        // Set value for autocomplete
        this.setDefaultForAutocomplete(this.formFields);
      });
    }
    // Reset Form Event
    if (this.formConfig?.resetEvent) {
      this.formConfig.resetEvent.subscribe((event) => {
        if (event) {
          // Reset Form
          this.form.reset();
          this.form.markAsPristine();
          this.form.markAsUntouched();
          this.form.updateValueAndValidity();
        }
      });
    }
  }

  /**
   * Process Form Data
   */
  private processFormData(): Promise<boolean> {
    return new Promise((resolve) => {
      // Page Loader Start
      this.isLoad = true;
      // Checking Default Data Source Existence
      if (this.formConfig.dataSource) {
        // Assigning data
        this.dataSource = this.formConfig.dataSource;
        // Page Loader Stop
        this.isLoad = false;
        // Resolving Promise
        resolve(true);
      } else {
        // If No Default Data Source Then Call API
        // Api Call
        this.formSubscription = this.apiService
          .request<any>({ ...this.formConfig?.getApi!, config: this.config })
          .subscribe(
            (res) => {
              // Assigning data
              this.dataSource = PpHelper.getValueWithApiKey(
                res,
                this.formConfig.apiDataKey!,
              );
              // Page Loader Stop
              this.isLoad = false;
              // Resolving Promise
              resolve(true);
            },
            (err) => {
              // Remove Loader
              this.isLoad = false;
              resolve(true);
            },
          );
      }
    });
  }

  /**
   *
   * @param formField : FormField
   * Set default value in case of edit
   */
  private setDefaultValue(formField: PpFormField, setDef?: boolean) {
    if (setDef === true) {
      formField.defaultValue = '';
    }
    // Checking value
    if (formField.value && formField.type != 'number') {
      // Setting value
      formField.defaultValue = this.formsService.getValueWithApiKey(
        this.dataSource,
        formField.value,
      );
    }
    if (formField.value && formField.type === 'number') {
      // Setting value
      formField.defaultValue = _.isEmpty(
        PpHelper.getValueWithApiKey(this.dataSource, formField.value),
      )
        ? undefined
        : parseFloat(
            this.dataParserService
              .getValue(this.dataSource, formField.value)
              .toString(),
          );
    }
    // Check for group
    if (formField.type === 'group') {
      // loop formfields and set value
      formField?.formFields?.forEach((groupField: PpFormField) => {
        this.setDefaultValue(groupField);
      });
    }
  }

  /**
   *
   * @param formFields :FormField[]
   * Check whether select fields has values from api
   */
  private checkValuesForSelectFields(formFields: PpFormField[]) {
    formFields.forEach(async (formField) => {
      // Checking form field type
      if (formField.type === 'group') {
        // Check values for select field in group and get in case of api
        formField['outline'] = true;
        this.checkValuesForSelectFields(formField.formFields!);
      } else if (formField.type === 'array') {
        formField['outline'] = true;
        (formField.formControl as unknown as FormArray)?.controls?.forEach(
          (control: any) => {
            this.checkValuesForSelectFields(control?.formFields);
          },
        );
      } else if (
        formField.type === 'select' ||
        (formField.type === 'tags' && formField?.tags?.type === 'select')
      ) {
        // Check for select type
        if (formField.select && formField.select.type === 'api') {
          // if (create mode) OR (edit without pagination) then make single call to load options
          if (
            !formField.select.mode ||
            formField.select.mode !== 'edit' ||
            !formField.select?.pagination
          ) {
            this.selectCurrentValueOptionsLoaded[formField.controlId!] = true;
            formField.values = [];
            // Set values
            await this.setValuesForSelectFields(formField);
            // Check for related fields
            if (formField.relatedTo && formField.relatedTo.length) {
              // Set related fields values
              this.getRelatedFieldsData(formField);
            }
          } else {
            // recusively load all options until default values are loaded
            if (formField.type === 'select' && formField.select) {
              formField.select.offSetComplete = false;
              formField.select.pagination.initialOffSet = '0';
              await new Promise((resolve) => {
                this.getAllValues(formField, resolve);
              });
              if (formField.relatedTo && formField.relatedTo.length) {
                // Set related fields values
                this.getRelatedFieldsData(formField);
              }
            }
          }
        }

        // Set Relative Values
        this.relativeSetValuesInForm(formField, this.form);
      }
    });
  }

  /**
   *
   * @param formFields :CanFormField[]
   */
  private setDefaultForAutocomplete(formFields: PpFormField[]) {
    formFields.forEach(async (formField) => {
      // Checking form field type
      if (formField.type === 'group') {
        // Check values for select field in group and get in case of api
        this.setDefaultForAutocomplete(formField.formFields!);
      } else if (formField.type === 'array') {
        formField['outline'] = true;
        (formField.formControl as unknown as FormArray)?.controls?.forEach(
          (control: any) => {
            this.setDefaultForAutocomplete(control?.formFields);
          },
        );
      } else if (formField.type === 'autocomplete') {
        // Check for select type
        if (formField.autoComplete && formField.defaultValue) {
          if (formField.autoComplete.type === 'local') {
            if (formField.values) {
              const index = formField.values.findIndex(
                (each) => each.value === formField.defaultValue,
              );
              if (index > -1) {
                formField.autoComplete.autocompleteSearchText =
                  formField.values[index].viewValue;
              }
            }
          } else if (
            formField &&
            formField.autoComplete &&
            formField.autoComplete.api &&
            formField.autoComplete.type === 'api'
          ) {
            formField.autoComplete.api.params = new PpHttpParams();
            const api: PpHttpOptions = Object.assign(
              {},
              formField.autoComplete.api,
            );
            if (!api.params) {
              api.params = new PpHttpParams();
            }
            api.params = api.params.append(
              formField.autoComplete.apiValueKey!,
              formField.defaultValue,
            );
            this.apiService
              .request<any>({ ...api, config: this.getConfig(api.config) })
              .subscribe((res) => {
                const data = PpHelper.getValueWithApiKey(
                  res,
                  formField.autoComplete?.apiDataKey!,
                );
                if (formField.autoComplete)
                  formField.autoComplete.dataSource = data;
                if (_.isArray(data) && formField && formField.autoComplete) {
                  // Set value
                  formField.autoComplete.autocompleteSearchText =
                    PpHelper.getValueWithApiKey(
                      data[0],
                      formField.autoComplete.apiViewValueKey!,
                      formField.autoComplete.viewValueSeparators,
                    );
                }
              });
          }
        }
      }
    });
  }

  /**
   *
   * @param formField : FormField
   * Set values in case of api in select fields
   */
  private setValuesForSelectFields(formField: PpFormField) {
    return new Promise((resolve) => {
      // Check for api
      if (formField && formField.select?.api) {
        // Api request
        this.loadSelectData(formField, (res: object) => {
          // Get data using key
          const data = PpHelper.getValueWithApiKey(
            res,
            formField.select?.apiDataKey!,
          );
          // Check whether data is array or not
          if (_.isArray(data) && formField.select) {
            // Assign dataSource
            formField.select.dataSource = data;
            // Loop data
            data.forEach((element) => {
              // If convertType is string, convert value to string
              const value =
                formField.select?.convertType === 'string'
                  ? this.dataParserService
                      .getValue(element, formField.select.apiValueKey!)
                      .toString()
                  : PpHelper.getValueWithApiKey(
                      element,
                      formField.select?.apiValueKey!,
                    );
              // Add object to values
              const { viewValue, secondaryViewValue } =
                this.getSelectOptionsValues(element, formField.select);

              formField.values?.push({
                value,
                viewValue,
                secondaryViewValue,
              });
            });
          }
          resolve(false);
        });
      } else {
        resolve(false);
      }
    });
  }

  /**
   *
   * @param formFields :CanFormField[]
   * Create Formgroup
   */
  private configureForm(formFields: PpFormField[]) {
    // Initialize group
    let group: any = {};
    // Loop form fields
    formFields.forEach((element) => {
      // Check for type
      if (element.type === 'group') {
        // Configure nested form
        let formFields = element.formFields;
        if (element.defaultValue && formFields?.length) {
          formFields.forEach((formField) => {
            if (formField.type === 'array' || formField.type === 'group') {
              formField.defaultValue = element.defaultValue[formField.name];
              (formField as any).isDefaultValueSetForConfiguration = true;
            }
          });
        }
        group[element.name] = this.configureForm(formFields!);
        if (element.defaultValue) {
          // Parse and set the upload i.e. image or document value in form
          this.formsService.setUploadValueInNestedForm(
            element.defaultValue,
            element.formFields,
          );
        }
        if (element.defaultValue) {
          group[element.name].patchValue(element.defaultValue);
        }
        element.formControl = group[element.name];
      } else if (element.type === 'array') {
        // Check for default value
        if (element.defaultValue && _.isArray(element.defaultValue)) {
          // Init formarray
          const formArray = new FormArray([]) as FormArray;
          // Loop on values
          element.defaultValue.forEach((each, index) => {
            // Parse and set the upload i.e. image or document value in form
            this.formsService.setUploadValueInNestedForm(
              each,
              element.formFields,
            );
            formArray.push(
              this.getNestedFormGroupForArrayElement(element, {
                defaultValue: each,
                copyPopulatedData: true,
                populatedDataIndex: index,
              }),
            );
          });
          // Assign formarray to group
          group[element.name] = formArray;
          // Assign default value
          group[element.name].patchValue(element.defaultValue);
        } else {
          // Assign formarray to group
          group[element.name] = new FormArray([
            this.getNestedFormGroupForArrayElement(element),
          ]);
        }
        element.formControl = group[element.name];
      } else {
        // Initialize validations
        const validators = this.getValidators(element);
        // Async validator
        let asyncValidators: any = [];
        if (element.type === 'text') {
          if (element.asyncValidation) {
            asyncValidators.push(this.asyncValidator.bind(this, element));
          }
        }

        if (element.type === 'image' || element.type === 'document') {
          element['suffixIcon'] =
            element.type === 'image'
              ? {
                  type: 1,
                  name: 'image',
                  tooltip: 'Add Image',
                  color: 'warn',
                }
              : {
                  type: 1,
                  name: 'insert_drive_file',
                  tooltip: 'Add File(s)',
                  color: 'warn',
                };
          element['outline'] = true;
          if (element.defaultValue) {
            if (!element.file?.multipleSelect) {
              element.defaultValue = {
                value: element.defaultValue,
                type: 'upload',
                viewValue:
                  this.formsService.getImageViewValue(element) ||
                  element.defaultValue,
              };
            } else if (element.file?.multipleSelect) {
              element.defaultValue = element.defaultValue?.map(
                (defValue: any) => {
                  return {
                    value: defValue,
                    type: 'upload',
                    viewValue: defValue,
                  };
                },
              );
            }
          }
        }
        // Add select all option to all values arrays of type multi-checkbox
        if (
          element.type === 'multicheckbox' &&
          element.values &&
          element.values.length &&
          element.values[0].value !== 'selectAll'
        ) {
          element.values.unshift({
            value: 'selectAll',
            viewValue: 'Select All',
          });
        }
        //if no pagination property in select, then dont emit scroll event
        if (element.type === 'select' && element.select) {
          element.select.offSetComplete = !element.select?.pagination;
        }
        // Create Form controls for each form field
        group[element.name] = new FormControl(
          { value: element.defaultValue, disabled: element.disabled },
          validators,
          asyncValidators,
        );

        element.formControl = group[element.name];
        const controlId = _.uniqueId('control_');
        element.controlId = controlId;

        if (element.type === 'select' && element.select?.searchable) {
          // adding control for select search field
          const searchControl = new FormControl('');
          group[`${controlId}_dropdownSearchText`] = searchControl;
          element.searchFormControl = searchControl;
          if (!element.select.api && element.values?.length) {
            // keeping backup of all options for local search
            this.selectAllValuesBackup[controlId] = element.values;
          }
        }
      }
      // Hide or show keys on basis of value
      this.hideFormFields(element.hideFormFields!, element.defaultValue);
    });
    return new FormGroup(group);
  }

  /**
   *
   * @param formField :CanFormField
   * Add validation to field
   */
  private getValidators(formField: PpFormField, flag?: boolean) {
    // Init validators
    const validators = [];
    let trueOrFalse = true;
    if (formField.type === 'select' && formField.select?.type === 'relative') {
      trueOrFalse = false;
    }
    if (flag === true) {
      trueOrFalse = true;
    }
    // If value is required, add required validation to array
    if (formField.required && formField.required.value && trueOrFalse) {
      validators.push(Validators.required);
    }
    // If there is pattern, add pattern validation to array
    if (
      formField.type === 'text' ||
      formField.type === 'number' ||
      formField.type === 'password'
    ) {
      if (formField.pattern) {
        validators.push(Validators.pattern(formField.pattern.value));
      }
    }
    // Check for relative validation
    if (formField.relativeValidation) {
      // Loop relativeValidation
      formField.relativeValidation.forEach((eachField) => {
        // Check for type
        switch (eachField.type) {
          case 'min':
            // Add relativeMinValidator to array
            validators.push(
              this.relativeMinValidator.bind(this, eachField, formField),
            );
            break;
          case 'max':
            // Add relativeMaxValidator to array
            validators.push(
              this.relativeMaxValidator.bind(this, eachField, formField),
            );
            break;
          case 'equals':
            // Add relativeEqualValidator to array
            validators.push(
              this.relativeEqualsValidator.bind(this, eachField, formField),
            );
            break;
          case 'notEquals':
            // Add relativeNotEqualsValidator to array
            validators.push(
              this.relativeNotEqualsValidator.bind(this, eachField, formField),
            );
            break;
          case 'required':
            // Add relativeRequiredValidator to array
            validators.push(
              this.relativeRequiredValidator.bind(this, eachField, formField),
            );
            break;
          case 'disabled':
            validators.push(
              this.relativeDisabledValidator.bind(this, eachField, formField),
            );
            break;
          case 'limit':
            validators.push(
              this.relativeSelectionLimit.bind(this, eachField, formField),
            );
            break;
          case 'formula':
            validators.push(
              this.relativeFormulaValidation.bind(this, eachField, formField),
            );
            break;
        }
      });
    }
    return validators;
  }

  /**
   *
   * @param validation :CanRelativeValidation
   * @param control :FormControl
   * Compare values and return result
   */
  private relativeMinValidator(
    validation: PpRelativeValidationMin,
    formField: PpFormField,
    control: FormControl,
  ) {
    // Check for form existence
    if (this.form) {
      const value = PpHelper.getValueWithApiKey(
        this.form.getRawValue(),
        validation.key,
      );
      if (value && control.value) {
        if (formField.type === 'date') {
          if (
            _.isNumber(Date.parse(value)) &&
            _.isNumber(Date.parse(control.value)) &&
            new Date(value) > new Date(control.value)
          ) {
            return { relativeMin: validation.errorMessage };
          }
        } else if (formField.type === 'text') {
          const numericValue = parseFloat(value);
          const controlNumericValue = parseFloat(control.value);

          if (!isNaN(numericValue) && !isNaN(controlNumericValue)) {
            if (numericValue > controlNumericValue) {
              return { relativeMin: validation.errorMessage };
            }
          }
        } else {
          if (value > control.value) {
            return { relativeMin: validation.errorMessage };
          }
        }
      }
    }
    return null;
  }

  /**
   *
   * @param validation :CanRelativeValidation
   * @param control :FormControl
   * Compare values and return result
   */
  private relativeMaxValidator(
    validation: PpRelativeValidationMax,
    formField: PpFormField,
    control: FormControl,
  ) {
    // Check for form existence
    if (this.form) {
      const value = PpHelper.getValueWithApiKey(
        this.form.getRawValue(),
        validation.key,
      );
      if (value && control.value) {
        if (formField.type === 'date') {
          if (
            _.isNumber(Date.parse(value)) &&
            _.isNumber(Date.parse(control.value)) &&
            new Date(value) < new Date(control.value)
          ) {
            return { relativeMax: validation.errorMessage };
          }
        } else if (formField.type === 'text') {
          const numericValue = parseFloat(value);
          const controlNumericValue = parseFloat(control.value);

          if (!isNaN(numericValue) && !isNaN(controlNumericValue)) {
            if (numericValue < controlNumericValue) {
              return { relativeMax: validation.errorMessage };
            }
          }
        } else {
          if (value < control.value) {
            return { relativeMax: validation.errorMessage };
          }
        }
      }
    }
    return null;
  }

  /**
   *
   * @param validation :CanRelativeValidation
   * @param control :FormControl
   * Compare values and return result
   */
  private relativeEqualsValidator(
    validation: PpRelativeValidationEquals,
    formField: PpFormField,
    control: FormControl,
  ) {
    // Check for form existence
    if (this.form) {
      const value = PpHelper.getValueWithApiKey(
        this.form.getRawValue(),
        validation.key,
      );
      if (value !== control.value) {
        return { relativeEqual: validation.errorMessage };
      }
    }
    return null;
  }

  /**
   *
   * @param validation :CanRelativeValidation
   * @param control :FormControl
   * Compare values and return result
   */
  private relativeNotEqualsValidator(
    validation: PpRelativeValidationNotEquals,
    formField: PpFormField,
    control: FormControl,
  ) {
    // Check for form existence
    if (this.form) {
      const value = PpHelper.getValueWithApiKey(
        this.form.getRawValue(),
        validation.key,
      );
      if (value && control.value && value === control.value) {
        return { relativeNotEqual: validation.errorMessage };
      }
    }
    return null;
  }

  /**
   *
   * @param validation :CanRelativeValidation
   * @param control :FormControl
   * Compare values and mark required
   */
  private relativeRequiredValidator(
    validation: PpRelativeValidationRequired,
    formField: PpFormField,
    control: FormControl,
  ) {
    const rawValues = validation.isRelative
      ? formField.parentFormGroup?.value
      : this.form?.getRawValue();
    // Check for form existence
    if (rawValues) {
      const value = PpHelper.getValueWithApiKey(rawValues, validation.key);
      // value - other field
      // control value - your field
      if (
        !PpHelper.isEmpty(value) &&
        !PpHelper.isEmpty(validation.value) &&
        value === validation.value
      ) {
        formField.required = { value: true };

        if (PpHelper.isEmpty(control.value) || _.isNaN(control.value)) {
          return { relativeRequired: validation.errorMessage };
        }
      } else {
        formField.required = undefined;
      }
    }
    return null;
  }

  /**
   *
   * @param validation :CanRelativeValidation
   * @param control :FormControl
   * Compare values and disable it
   */
  private relativeDisabledValidator(
    validation: PpRelativeValidationDisabled,
    formField: PpFormField,
    control: FormControl,
  ) {
    if (this.form) {
      const value = PpHelper.getValueWithApiKey(
        this.form.getRawValue(),
        validation.key,
      );

      const isValueArray = Array.isArray(value);
      const isValidationValueArray = Array.isArray(validation.value);

      let shouldDisable = false;

      if (isValueArray && isValidationValueArray) {
        shouldDisable = validation.value.some((val: string) =>
          value.includes(val),
        );
      } else if (isValueArray) {
        shouldDisable = value.includes(validation.value);
      } else if (isValidationValueArray) {
        shouldDisable = validation.value.includes(value);
      } else {
        shouldDisable = value === validation.value;
      }

      formField.disabled = shouldDisable || undefined;
    }
    return null;
  }

  disabledMessage(formField: PpFormField): string | undefined {
    return formField.relativeValidation?.find(
      (validation) => validation.type === 'disabled',
    )?.errorMessage;
  }

  /**
   *
   * @param validation :CanRelativeValidation
   * @param control :FormControl
   * Compare values and mark selection limit
   */
  private relativeSelectionLimit(
    validation: PpRelativeValidationLimit,
    formField: PpFormField,
    control: FormControl,
  ) {
    // Check for form existence
    if (this.form) {
      const value = PpHelper.getValueWithApiKey(
        this.form.getRawValue(),
        validation.key,
      );
      // value - other field
      // control value - your field
      if (value && validation.value && value === validation.value) {
        if (
          formField.type === 'select' &&
          formField.multipleSelect &&
          validation.limit
        ) {
          formField.selectionLimit = {
            value: validation.limit,
            message: validation.errorMessage!,
          };
        }
      } else {
        delete formField?.selectionLimit;
      }
    }
    return null;
  }

  /**
   *
   * @param validation :CanRelativeValidation
   * @param control :FormControl
   * Compare multiple values with a formula for comparison
   */
  private relativeFormulaValidation(
    validation: PpRelativeValidationFormula,
    formField: PpFormField,
    control: FormControl,
  ) {
    // Check for form existence
    if (this.form) {
      const itemsArray = validation.keys.map((key: string) => {
        return {
          key,
          value: PpHelper.getValueWithApiKey(this.form.getRawValue(), key),
        };
      });

      let formulaString = validation.formula;
      itemsArray.forEach((item: any) => {
        formulaString = formulaString.replace(item.key, item.value || 0);
      });

      const formulaEval = eval(formulaString);

      if (validation.operator === 'max' && formulaEval < control.value) {
        return { relativeMax: `${validation.errorMessage} ${formulaEval}` };
      }

      if (validation.operator === 'min' && formulaEval > control.value) {
        return { relativeMin: `${validation.errorMessage} ${formulaEval}` };
      }

      if (
        validation.operator === 'maxNoEqual' &&
        formulaEval <= control.value
      ) {
        return { relativeMax: `${validation.errorMessage} ${formulaEval}` };
      }

      if (
        validation.operator === 'minNoEqual' &&
        formulaEval >= control.value
      ) {
        return { relativeMin: `${validation.errorMessage} ${formulaEval}` };
      }
    }
    return null;
  }

  /**
   *
   * @param formField :CanFormField
   * @param control :FormControl
   */
  private asyncValidator(formField: PpFormField, control: FormControl) {
    return new Promise((resolve) => {
      // Check for asyncValidateSubscription
      // if (this.asyncValidateSubscription) {
      //   this.asyncValidateSubscription.unsubscribe();
      // }
      // Check for form
      if (this.form && formField.asyncValidation) {
        formField.asyncValidation.minCharacters =
          formField.asyncValidation.minCharacters || 1;

        if (
          control.value &&
          control.value.trim() &&
          control.value.length >= formField.asyncValidation.minCharacters
        ) {
          if (!formField.asyncValidation.api.params) {
            formField.asyncValidation.api.params = new PpHttpParams();
          }
          formField.asyncValidation.api.params =
            formField.asyncValidation.api.params?.set(
              formField.asyncValidation.apiParamKey,
              control.value,
            );
          this.asyncValidateSubscription = this.apiService
            .request({
              ...formField.asyncValidation.api,
              config: this.getConfig(formField.asyncValidation.api.config),
            })
            .subscribe(
              (res: any) => {
                const data = PpHelper.getValueWithApiKey(
                  res,
                  formField.asyncValidation?.apiDataKey!,
                );
                if (
                  !this.conditionValidationService.validate(
                    formField.asyncValidation?.condition!,
                    data,
                  )
                ) {
                  resolve({
                    asyncValidationError:
                      formField.asyncValidation?.errorMessage,
                  });
                } else {
                  resolve(null);
                }
              },
              (err: any) => {
                resolve(null);
              },
            );
        } else {
          resolve(null);
        }
      } else {
        resolve(null);
      }
    });
  }

  /**
   *
   * @param control :AbstractControl
   * Validate every field after any change
   */
  private validateAllFields(control: AbstractControl) {
    // Check control type is Formgroup or not
    if (control instanceof FormGroup) {
      // Def Formgroup
      const group = control as FormGroup;
      // Loop on formcontrols in formgroup
      for (const field in group.controls) {
        // Def control
        const c = group.controls[field];
        // Recursive call
        this.validateAllFields(c);
      }
    } else {
      // Validate control
      control.updateValueAndValidity({ onlySelf: false });
    }
  }

  async loadImageAndCheckDimensions(event: any, control: PpFormField) {
    return new Promise((resolve, reject) => {
      const imgSrc = URL.createObjectURL(event.target.files[0]);
      const img = new Image();
      img.src = imgSrc;
      img.onload = function () {
        let width = img.width;
        let height = img.height;
        const dimensions = control?.file?.dimensions;

        if (dimensions) {
          const { width: widthConstraints, height: heightConstraints } =
            dimensions;
          const { min: minWidth, max: maxWidth } = widthConstraints || {};
          const { min: minHeight, max: maxHeight } = heightConstraints || {};

          const widthValid =
            (!minWidth || width >= minWidth) &&
            (!maxWidth || width <= maxWidth);
          const heightValid =
            (!minHeight || height >= minHeight) &&
            (!maxHeight || height <= maxHeight);

          if (widthValid && heightValid) {
            resolve(false); // Dimensions match constraints
          } else {
            resolve(true); // Dimensions do not match constraints
          }
        } else {
          resolve(true); // No constraints, so it's valid
        }
      };
    });
  }

  private getDimentionErrorMessage(dimensions: ImageDimension): string {
    const { width: widthConstraints, height: heightConstraints } = dimensions;
    const { min: minWidth, max: maxWidth } = widthConstraints || {};
    const { min: minHeight, max: maxHeight } = heightConstraints || {};
    return (
      (minWidth || maxWidth || minHeight || maxHeight
        ? 'Please upload with '
        : 'Dimension config is not valid.') +
      (minWidth && maxWidth
        ? `min ${minWidth}px width to max ${maxWidth}px width`
        : minWidth
          ? `min ${minWidth}px width`
          : maxWidth
            ? `max ${minWidth}px width`
            : '') +
      (minWidth || maxWidth ? ' and ' : '.') +
      (minHeight && maxHeight
        ? `min ${minHeight}px height to max ${maxHeight}px height.`
        : minHeight
          ? `min ${minHeight}px height.`
          : maxHeight
            ? `max ${minWidth}px height.`
            : '')
    );
  }

  /**
   *
   * @param event :Event
   * @param control :CanFormField
   * @param spinner
   * @param input
   * @param form :FormGroup
   * Upload File and add res file path to form value
   */
  async upload(
    event: any,
    control: PpFormField,
    spinner: any,
    input: any,
    form: FormGroup,
  ) {
    if (event.target.files && event.target.files.length) {
      if (control?.type === 'image' && control?.file?.acceptType === '*/*') {
        control.file.acceptType = 'image/*';
      }
      // Compare config accept-type(s) with input file type...
      const acceptTypeFromConfig = control.file?.acceptType
        ? control.file.acceptType
        : '';
      const fileExtension: string = event.target.files[0]?.name
        ? event.target.files[0].name.split('.').pop()
        : '';
      const acceptTypeFromInput: string = event.target.files[0]?.type
        ? event.target.files[0].type
        : '';
      if (
        control.file &&
        !control.file?.sizeLimitInMbs &&
        control.type === 'image'
      ) {
        control.file.sizeLimitInMbs = MAX_IMAGE_SIZE;
      }
      if (control.file && !control.file.multipleSelect) {
        const sizeInMbs = event.target.files[0].size / 1000 / 1000;
        if (
          control.file?.sizeLimitInMbs &&
          sizeInMbs > control.file?.sizeLimitInMbs
        ) {
          return this.showInvalidFileFormatError({
            control,
            event,
            form,
            input,
            message: `Size is too big. Please upload below ${control.file?.sizeLimitInMbs}MB`,
          });
        }
        if (control.file?.dimensions) {
          if (await this.loadImageAndCheckDimensions(event, control)) {
            return this.showInvalidFileFormatError({
              control,
              event,
              form,
              input,
              message: this.getDimentionErrorMessage(control.file?.dimensions),
            });
          }
        }
        if (control?.type === 'image') {
          /*
          Condition for throwing error
          */
          if (!acceptTypeFromInput.includes('image/')) {
            return this.showInvalidFileFormatError({
              control,
              event,
              form,
              input,
              message: `Invalid file format. Valid formats: ${control?.file?.acceptType}`,
            });
          } else if (
            acceptTypeFromConfig !== 'image/*' &&
            !acceptTypeFromConfig.includes(fileExtension)
          ) {
            return this.showInvalidFileFormatError({
              control,
              event,
              form,
              input,
              message: `Invalid file format. Valid formats: ${control?.file?.acceptType}`,
            });
          }
        } else if (
          control?.type === 'document' &&
          !acceptTypeFromConfig.includes('*/*') &&
          !acceptTypeFromConfig.includes(fileExtension)
        ) {
          return this.showInvalidFileFormatError({
            control,
            event,
            form,
            input,
            message: `Invalid file format. Valid formats: ${control?.file?.acceptType}`,
          });
        }
      } else {
        // Loop over Files
        for (let i = 0; i < event.target.files.length; i++) {
          let acceptType = event.target.files[i].type;
          let fileExtension = event.target.files[i].name.split('.').pop();
          const sizeInMbs = event.target.files[i].size / 1000 / 1000;
          if (
            control.file?.sizeLimitInMbs &&
            sizeInMbs > control.file?.sizeLimitInMbs
          ) {
            return this.showInvalidFileFormatError({
              control,
              event,
              form,
              input,
              message: `Size is too big. Please upload below ${control.file?.sizeLimitInMbs}MB`,
            });
          }
          if (control.type === 'image') {
            if (control.file?.dimensions) {
              if (await this.loadImageAndCheckDimensions(event, control)) {
                return this.showInvalidFileFormatError({
                  control,
                  event,
                  form,
                  input,
                  message: this.getDimentionErrorMessage(
                    control.file?.dimensions,
                  ),
                });
              }
            }
            if (!acceptType.includes('image/')) {
              return this.showInvalidFileFormatError({
                control,
                event,
                form,
                input,
                message: `Invalid file format. Valid formats: ${control?.file?.acceptType}`,
              });
            } else if (
              acceptTypeFromConfig !== 'image/*' &&
              !acceptTypeFromConfig.includes(fileExtension)
            ) {
              return this.showInvalidFileFormatError({
                control,
                event,
                form,
                input,
                message: `Invalid file format. Valid formats: ${control?.file?.acceptType}`,
              });
            }
          } else if (
            control?.type === 'document' &&
            !acceptTypeFromConfig.includes('*/*') &&
            !acceptTypeFromConfig.includes(fileExtension)
          ) {
            return this.showInvalidFileFormatError({
              control,
              event,
              form,
              input,
              message: `Invalid file format. Valid formats: ${control?.file?.acceptType}`,
            });
          }
        }
      }
      // Check for spinner
      if (spinner) {
        // Visible spinner
        spinner._elementRef.nativeElement.style.display = 'block';
      }

      // initialze form data
      const formData: FormData = new FormData();
      // Loop over Files
      for (let i = 0; i < event.target.files.length; i++) {
        // file def's
        const file = event.target.files[i];
        const fileNme = event.target.files[i].name;
        // add file to formdata
        formData.set(control.file?.payloadName!, file, file.name);

        const extension = file.name.split('.').pop();

        const contentTypeMap: Record<string, string> = {
          glb: 'model/gltf-binary',
          lottie: 'application/json',
        };

        const contentType =
          !file.type && contentTypeMap.hasOwnProperty(extension)
            ? contentTypeMap[extension]
            : file.type;

        const s3SignedUploadBody = {
          type: 'ADMIN',
          name: fileNme,
          extension,
          contentType,
        };

        const isS3Signed = control.file?.isS3Signed !== false;
        const isGetMethod = control.file?.api?.method === 'GET';

        // call the api
        this.uploadSubscription = this.apiService
          .request(
            {
              ...control.file?.api!,
              config: this.getConfig(control.file?.api?.config),
              ...(isS3Signed && isGetMethod
                ? {
                    params: new PpHttpParams()
                      .append('type', 'ADMIN')
                      .append('name', file.name)
                      .append('extension', extension)
                      .append('contentType', contentType),
                  }
                : {}),
            },
            isS3Signed ? s3SignedUploadBody : formData,
          )
          .subscribe(
            async (res: any) => {
              let uploadResponse;
              let modifiedResponse = res;

              if (
                control?.file?.isS3Signed?.toString() !== 'false' &&
                res?.success
              ) {
                uploadResponse = await uploadSignedURLJSON(res?.data, file);

                if (!uploadResponse?.ok) {
                  return this.notificationService.showError(
                    `Failed to upload file to S3: ${uploadResponse?.statusText}`,
                  );
                }
                modifiedResponse = {
                  ...res,
                  data: {
                    ...res?.data,
                    fullUrl:
                      (res?.data?.baseUrl || '') + (res?.data?.key || ''),
                  },
                };
              }

              const value = PpHelper.getValueWithApiKey(
                modifiedResponse,
                control.file?.apiKey!,
              );
              const imageValue = modifiedResponse.data.fullUrl;
              const uploadValue = {
                value: value,
                viewValue: imageValue,
                type: 'upload',
                name: fileNme,
              };

              if (control.file?.multipleSelect) {
                // Check whether the value is array or not
                if (!_.isArray(form.controls[control.name].value)) {
                  form.controls[control.name].setValue([]);
                }
                const existingValue = form.controls[control.name].value;
                form.controls[control.name].setValue([
                  ...existingValue,
                  uploadValue,
                ]);
              } else {
                form.controls[control.name].setValue(uploadValue);
              }
              // Form Value Change
              this.formValueChange(control, form);
              // Check for spinner
              if (spinner) {
                // Hide spinner
                spinner._elementRef.nativeElement.style.display = 'none';
              }
            },
            (err: any) => {
              this.notificationService.showError(
                err?.error?.message || 'Failed to get S3 signed URL',
              );
              // Check for spinner
              if (spinner) {
                // Hide spinner
                spinner._elementRef.nativeElement.style.display = 'none';
              }
            },
          );
      }
      // clear input
      input.clear();
    }
  }

  /**
   *
   * @param control : formField Config
   * @param event : event Object
   * @param form : FormGroup
   * @param input : #input element
   *
   * Show error for invalid files.
   */
  showInvalidFileFormatError({
    control,
    event,
    form,
    input,
    message,
  }: {
    control: PpFormField;
    event: any;
    form: FormGroup;
    input: any;
    message: string;
  }): void {
    this.notificationService.showError(message);
    if (control?.file?.viewValue?.value) {
      delete control.file.viewValue.value;
      event.stopPropagation();
      form.controls[control.name].setValue(undefined);
      this.formValueChange(control, form);
    }
    input.clear();
    return;
  }
  /**
   *
   * @param key : string
   * @param index : number
   *
   * Remove image or doc from form value
   */
  public removeFile(
    event: any,
    control: PpFormField,
    index: number,
    form: FormGroup,
  ) {
    event.stopPropagation();
    form.controls[control.name].value.splice(index, 1);
  }

  /**
   *
   * @param event :MatChipInputEvent
   * @param key :string
   * @param form :FormGroup
   * Add tag to form control
   */
  public addTags(
    event: MatChipInputEvent,
    control: PpFormField,
    form: FormGroup,
  ): void {
    const input = event.input;
    const value = event.value;

    // Add tag
    if ((value || '').trim()) {
      // If array then push the new value
      if (_.isArray(form.controls[control.name].value)) {
        form.controls[control.name].value.push(value);
        // Else create new array with value
      } else {
        form.controls[control.name].setValue([value]);
      }
    }

    // Reset the input value
    if (input) {
      input.value = '';
    }
  }

  /**
   *
   * @param value
   * @param control :CanFormField
   * @param input
   * @param form :FormGroup
   * Add tag to form control from autocomplete
   */
  public addTagsFromAutocomplete(
    value: any,
    control: PpFormField,
    input: any,
    form: FormGroup,
  ) {
    // If array then push the new value
    if (_.isArray(form.controls[control.name].value)) {
      form.controls[control.name].value.push(value);
      // Else create new array with value
    } else {
      form.controls[control.name].setValue([value]);
    }
    // Reset the input value
    input.value = '';
  }

  /**
   *
   * @param key : any
   * @param values :CanValueItem[]
   * Return Value to be displayed as tag
   */
  public getDisplayValueForTags(key: any, values: PpValueItem[]) {
    // Check for values
    if (values && values.length) {
      // Find value index
      const index = values.findIndex((val) => val.value === key);
      // If value present
      if (index > -1) {
        // Return display value
        return values[index].viewValue;
      }
    }
    return undefined;
  }

  /**
   *
   * @param options :CanValueItem[]
   * @param selectedValues :Array<any>
   * Filter values that are not selected
   */
  public getDisplayOptionsForTags(
    options: PpValueItem[],
    selectedValues: Array<any>,
  ) {
    return options.filter((option) => {
      // Check for selectedValues
      if (selectedValues && selectedValues.length) {
        // Find index in selectedValues
        const index = selectedValues.findIndex((each) => each === option.value);
        // If value is selected then remove from array
        if (index > -1) {
          return false;
        }
      }
      return true;
    });
  }

  toggleSelectAll(control: any) {
    if (control.formControl?.value?.length !== control?.values?.length) {
      control.formControl.setValue(
        control.values.map((ele: PpValueItem) => ele.value),
      );
    } else {
      control.formControl.setValue([]);
    }
  }

  /**
   *
   * @param formField :CanFormField
   * Emit when form value changes
   */
  public formValueChange(formField: PpFormField, form: FormGroup): void {
    // Emit Before Submit Event to Parent
    this.beforeSubmitFormValue.emit(form.value);
    // Set Form Dirty;
    this.setFormDirty(form);
    // Set value change in form control
    this.formsService.setFormControlDirty(formField, form);
    // change Relative Values that are affected by this field
    this.changeRelativeValues(formField, form);
    // Validate all fields on any change
    this.validateAllFields(this.form);
    // Hide or show keys on basis of value
    this.hideFormFields(
      formField.hideFormFields!,
      form.controls[formField.name].value,
    );
  }

  /**
   *
   * @param formField: CanFormField
   * @param form: FormGroup
   */
  private changeRelativeValues(formField: PpFormField, form: FormGroup) {
    // Check for relative Set Value fields
    if (formField.relativeSetFields && formField.relativeSetFields.length) {
      formField.relativeSetFields.forEach((element) => {
        // Check type
        if (element.type === 'same') {
          // Set same field value to them
          let value: any = '';
          if (element.value && !formField.select) {
            const data = formField.values?.find(
              (item) => item.value === form.controls[formField.name].value,
            );
            value = this.formsService.getValueWithApiKey(data, element.value);
          } else {
            value = this.formsService.getValueWithApiKey(
              form.getRawValue(),
              element.value || formField.name,
            );
            if (formField.type === 'image' || formField.type === 'document') {
              if (Array.isArray(value)) {
                value = value.map((row) => {
                  if (row?.type === 'upload') {
                    return row.value;
                  }
                  return row;
                });
              } else if (value?.type === 'upload') {
                value = value.value;
              }
            }
          }
          this.formsService.setFormFieldValue(element.key, value, form);
        } else if (element.type === 'different') {
          // Check differentType
          if (element.differentType === 'dataSource') {
            // Check for datasource
            if (
              formField.type === 'select' &&
              formField.select &&
              formField.select.dataSource
            ) {
              const data = this.formsService.getObjectFromArray(
                formField.select.dataSource,
                formField.select.apiValueKey!,
                form.controls[formField.name].value,
              );
              const value = this.formsService.getValueWithApiKey(
                data!,
                element.value!,
              );
              this.formsService.setFormFieldValue(element.key, value, form);
            } else if (
              formField.type === 'autocomplete' &&
              formField.autoComplete &&
              formField.autoComplete.dataSource
            ) {
              const data = this.formsService.getObjectFromArray(
                formField.autoComplete.dataSource,
                formField.autoComplete.apiValueKey!,
                form.controls[formField.name].value,
              );
              const value = this.formsService.getValueWithApiKey(
                data!,
                element.value!,
              );
              this.formsService.setFormFieldValue(element.key, value, form);
            }

            // Remove value
            if (formField.select && !formField.select.dataSource) {
              this.formsService.setFormFieldValue(element.key, undefined, form);
            }
          } else if (
            element.differentType === 'local' &&
            typeof element.differentValueExtractor === 'function'
          ) {
            const sourceValue = this.formsService.getValueWithApiKey(
              form.getRawValue(),
              formField.name,
            );
            const targetValue = element.differentValueExtractor({
              selfValue: sourceValue,
              formGroupValue: form.getRawValue(),
              formValue: this.form.getRawValue(),
            });
            this.formsService.setFormFieldValue(element.key, targetValue, form);
          }
        } else if (element.type === 'auto-pickup') {
          // Set same field value to them
          const value = this.formsService.getValueWithApiKey(
            form.getRawValue(),
            formField.name,
          );
          const regexMapper = [
            {
              regex:
                /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/)|youtu\.be\/)[\w-]{11}(\?[\w-]+=[\w-]+(&[\w-]+=[\w-]+)*)?$/,
              value: 'youtube',
            },
            {
              regex:
                /(http|https)?:\/\/(www\.|player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|video\/|)(\d+)(?:|\/\?)/,
              value: 'vimeo',
            },
            {
              regex: /^.*zoom\.us\/.*$/,
              value: 'zoom',
            },
            {
              regex: /^https:\/\/.*master\.mpd$/,
              value: 'penpencilvdo',
            },
            {
              regex: /^https:\/\/.*\.m3u8$/,
              value: 'awsVideo',
            },
          ];
          regexMapper.every((regexObj, index) => {
            if (regexObj.regex.test(value)) {
              this.formsService.setFormFieldValue(
                element.key,
                regexObj.value,
                form,
              );
              return false;
            } else if (index === regexMapper.length - 1) {
              this.formsService.setFormFieldValue(element.key, 'none', form);
              return false;
            }
            return true;
          });
        }
      });
    }
    // Check for related fields
    if (formField.relatedTo && formField.relatedTo.length) {
      // Set related fields options. Also reset the selected values for the related fields
      this.getRelatedFieldsData(formField, { resetValue: true });
    }

    // Set Relative Values
    this.relativeSetValuesInForm(formField, form);

    // Set Dynamic Values
    this.relativeSetDynamicallyInForm(formField, form);
  }

  private relativeSetValuesInForm(
    formField: PpFormField,
    form: FormGroup,
  ): void {
    if (formField.relativeSetValues?.length) {
      formField.relativeSetValues.forEach((element) => {
        if (formField.multipleSelect) {
          const selectValues = form.value[formField.name];
          const selectedList: any = [];

          selectValues.forEach((selectValue: string) => {
            const selectedObj = formField?.values?.find((value) => {
              return value?.value === selectValue;
            });
            if (selectedObj) {
              selectedList.push(selectedObj);
            }
          });
          const relativeFormFields = this.findRelativeFormField(
            this.formFields,
            element.key,
          );
          if (relativeFormFields.length && selectedList?.length) {
            relativeFormFields.forEach((field) => {
              field.values = selectedList;
            });
          }
        }
      });
    }
  }

  private relativeSetDynamicallyInForm(
    formField: PpFormField,
    form: FormGroup,
  ): void {
    if (!!formField.relativeSetDynamically) {
      if (form) {
        const itemsArray = formField.relativeSetDynamically.keys.map(
          (key: string) => {
            return {
              key,
              value: PpHelper.getValueWithApiKey(form.getRawValue(), key),
            };
          },
        );

        let formulaString = formField.relativeSetDynamically.formula;
        itemsArray.forEach((item: any) => {
          formulaString = formulaString.split(item.key).join(item.value || 0);
        });
        try {
          const formulaEval = eval(formulaString);
          this.formsService.setFormFieldValue(
            formField.relativeSetDynamically.key,
            formulaEval,
            form,
          );
        } catch (e) {
          console.log(e);
        }
      }
    }
  }

  private findRelativeFormField(
    formFields: PpFormField[],
    key: string,
  ): PpFormField[] {
    if (key.includes('.')) {
      const keys = key.split('.');
      const firstLevelKey = keys[0];
      const remainingKeys = keys.slice(1).join('.');
      const firstLevelFormFields = formFields.find(
        (field) => field?.name == firstLevelKey,
      );
      if (firstLevelFormFields?.type === 'array') {
        const fieldControls = (
          this.form.controls[firstLevelFormFields.name] as FormArray
        ).controls;
        const relativeFields: PpFormField[] = [];
        if (fieldControls.length) {
          fieldControls.forEach((control) => {
            relativeFields.push.apply(
              relativeFields,
              this.findRelativeFormField(
                (control as any).formFields!,
                remainingKeys,
              ),
            );
          });
        }
        relativeFields.push.apply(
          relativeFields,
          this.findRelativeFormField(
            firstLevelFormFields.formFields!,
            remainingKeys,
          ),
        );
        return relativeFields;
      } else {
        return this.findRelativeFormField(
          firstLevelFormFields?.formFields!,
          remainingKeys,
        );
      }
    } else {
      return [formFields.find((field) => field?.name == key) as PpFormField];
    }
  }

  /**
   *
   * @param formField :CanFormField
   * Set Related Fields Values
   */
  private async getRelatedFieldsData(
    formField: PpFormField,
    options: { resetValue?: boolean } = {},
  ) {
    // Loop relatedTo keys
    for (let element of formField?.relatedTo || []) {
      const val = this.getRelativeFormField(element, formField);
      let relatedField: any;
      relatedField = val === false ? '' : val;
      // Check for relatedField
      if (relatedField) {
        // Check for type
        if (
          relatedField.type === 'select' ||
          (relatedField.type === 'tags' && relatedField.tags?.type === 'select')
        ) {
          // Check for select type 'relative
          if (relatedField.select && relatedField.select.type === 'relative') {
            const validators = this.getValidators(relatedField, true);
            (relatedField.formControl as FormControl)?.setValidators(
              validators,
            );
            if (options.resetValue) {
              // resetting selected values if source field changed. resetValue flag is true only when called from changeRelativeValues
              (relatedField.formControl as FormControl)?.setValue(
                relatedField.multipleSelect ? [] : '',
                { emitModelToViewChange: false },
              );
            }
            // if edit mode with pagination, then recursively fetch options until selected values are loaded
            if (
              relatedField.select?.mode === 'edit' &&
              relatedField.select.pagination
            ) {
              relatedField.select.pagination.initialOffSet = '0';
              this.getAllRelatedValues(relatedField, formField, true);
            } else {
              let emptyVariables = false;
              // To remove instance of object
              const api: PpHttpOptions = Object.assign(
                {},
                relatedField.select.api,
              );
              // Get variables from url
              const variables = PpHelper.getVariablesFromUrl(api.apiPath);
              // Replace each variable with value
              for (const variable of variables) {
                const value = PpHelper.getValueWithApiKey(
                  formField.isArrayChild
                    ? formField.parentFormGroup?.getRawValue()
                    : this.form.getRawValue(),
                  variable,
                );
                // If value is not empty, set value
                if (!PpHelper.isEmpty(value)) {
                  api.apiPath = api.apiPath.replace(
                    '${' + variable + '}',
                    value,
                  );
                } else {
                  // Else, set emptyVariables to true and break loop
                  emptyVariables = true;
                  break;
                }
              }
              // Check for emptyVariables
              if (!emptyVariables) {
                // Check for query params
                const queryParams =
                  relatedField.select.relativeApiInfo &&
                  relatedField.select.relativeApiInfo.queryParams
                    ? relatedField.select.relativeApiInfo.queryParams
                    : undefined;
                if (queryParams && queryParams.length) {
                  // Check for api params
                  if (!api.params) {
                    // Init params
                    api.params = new PpHttpParams();
                  }
                  for (const eachParam of queryParams) {
                    // Get Param Value
                    const value = PpHelper.mapValueWithApiKey(
                      formField.isArrayChild
                        ? formField.parentFormGroup?.getRawValue()
                        : this.form.getRawValue(),
                      eachParam.value,
                    );
                    // If value is not empty, set value
                    if (!PpHelper.isEmpty(value)) {
                      api.params = api.params.set(
                        eachParam.key,
                        this.getRelativeQueryParamValue(value, eachParam),
                      );
                    } else {
                      if (!eachParam.isOptional) {
                        // Else, set emptyVariables to true and break loop
                        emptyVariables = true;
                        break;
                      }
                    }
                  }
                }
                // If there is no empty variables
                if (!emptyVariables) {
                  // Init related field values
                  relatedField.values = [];
                  this.selectCurrentValueOptionsLoaded[
                    relatedField.controlId!
                  ] = true;

                  if (api.params) {
                    if (api.params.get('page')) {
                      api.params = api.params.set('page', '1');
                    }
                    // reset search text
                    if (
                      relatedField.select?.searchable &&
                      relatedField.select?.searchConfig?.searchKey
                    ) {
                      api.params = api.params.delete(
                        relatedField.select.searchConfig.searchKey,
                      );
                      (relatedField.searchFormControl as FormControl)?.setValue(
                        '',
                        {
                          emitViewToModelChange: false, // to avoid search text changed event
                          emitEvent: false,
                        },
                      );
                    }
                  }
                  // Api request
                  this.loadSelectData(
                    {
                      ...relatedField,
                      select: {
                        ...relatedField.select,
                        api,
                      },
                    },
                    (res: any) => {
                      // get data
                      const data = relatedField.select?.apiDataKey
                        ? PpHelper.getValueWithApiKey(
                            res,
                            relatedField.select?.apiDataKey!,
                          )
                        : res?.data;
                      // Check whether data is array or not
                      if (_.isArray(data) && relatedField.select) {
                        // Assign dataSource
                        relatedField.select.dataSource = data;
                        data.forEach((element) => {
                          // Check for convert type 'string'. If yes, then convert it to string.
                          const value =
                            relatedField.select?.convertType === 'string'
                              ? this.dataParserService
                                  .getValue(
                                    element,
                                    relatedField.select?.apiValueKey!,
                                  )
                                  .toString()
                              : PpHelper.getValueWithApiKey(
                                  element,
                                  relatedField.select?.apiValueKey!,
                                );
                          // Push object to values
                          const { viewValue, secondaryViewValue } =
                            this.getSelectOptionsValues(
                              element,
                              relatedField.select,
                            );
                          relatedField.values?.push({
                            value,
                            viewValue,
                            secondaryViewValue,
                          });
                        });
                      }
                    },
                  );
                }
              }
            }
          }
        }
      }
    }
  }

  /**
   *
   * @param name : string
   * Get Relative Form Field
   */
  private getRelativeFormField(name: string, source?: PpFormField) {
    // Return false in case of empty
    if (PpHelper.isEmpty(name)) {
      return false;
    }
    // Storing the nested key in array
    const keys = name.split('.');
    let targetFields = this.formFields;
    if (source?.isArrayChild && source?.siblings) {
      targetFields = source.siblings;
    }
    // Def FormField
    let formField: PpFormField = this.formsService.findFormField(
      targetFields,
      keys[0],
    );
    // If found
    if (formField) {
      // Check for keys length
      if (keys.length === 1) {
        // Return formfield
        return formField;
      } else {
        // Nested Object
        // Loop keys from index 1 as 0 is already assigned
        for (let i = 1; i < keys.length; i++) {
          // Check for formField type 'group'
          if (formField.type === 'group') {
            // Find index in group form fields
            const groupFieldIndex = formField.formFields?.findIndex(
              (each) => each.name === keys[i],
            );
            // If found
            if (
              groupFieldIndex &&
              groupFieldIndex > -1 &&
              formField.formFields
            ) {
              // Set that form field
              formField = formField.formFields[groupFieldIndex];
            } else {
              // Return in case of not found
              return false;
            }
          } else {
            // Return in case of not found
            return false;
          }
        }
        // Check the formField type, if it is 'group', then return false
        if (formField.type !== 'group') {
          return formField;
        } else {
          return false;
        }
      }
    } else {
      return false;
    }
  }

  onSubmit(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    if (this.formConfig?.showPreview) {
      const dialogRef = this.dialog.open(PreviewComponent, {
        disableClose: false,
        width: 'auto',
        maxHeight: '90vh',
        panelClass: 'custom-action-sizing',
        data: this.formsService.makePreviewObject(this.formConfig.formFields),
      });
      dialogRef.afterClosed().subscribe((response) => {
        if (response) {
          this.submitForm();
        }
      });
    } else {
      this.submitForm();
    }
  }

  /**
   * Submit Form
   */
  public submitForm(): void {
    // Disable Submit Button
    this.disableSubmitButton = true;
    let valuesObj;
    if (this.formConfig.sendAll) {
      valuesObj = Object.assign({}, this.form.getRawValue());
    } else {
      valuesObj = this.formsService.getAllEditedValues(this.form);
    }
    if (
      this.formConfig.submitApi?.relativeApiInfo &&
      this.formConfig.submitApi.relativeApiInfo?.queryParams &&
      this.formConfig.submitApi?.relativeApiInfo?.queryParams?.length
    ) {
      const queryKeys =
        this.formConfig.submitApi.relativeApiInfo.queryParams.map(
          (el) => el.value,
        );

      let obj: any = {};
      _.forEach(this.formFields, (formField) => {
        if (queryKeys.includes(formField.name)) {
          obj[formField.name] = this.form.controls[formField.name].value;
        }
      });
      let param = new PpHttpParams();
      this.formConfig.submitApi.relativeApiInfo.queryParams.forEach((el) => {
        param = param.append(`${el.key}`, `${obj[el.value]}`);
      });

      this.formConfig.submitApi.params = param;
    }
    // Remove values that are not required to send
    this.formsService.removeNotIncludedValues(
      this.formFields,
      valuesObj,
      this.form,
    );
    // Remove 'selectAll' value from multicheckbox
    this.removeAllFromMultiCheckBox(this.formFields, valuesObj, this.form);
    // Remove search text field for searchable select
    this.removeSelectSearchTextFields(valuesObj);
    // Parse Image upload values
    this.formsService.parseUploadValue(this.formFields, valuesObj, this.form);
    // Check for stepper form
    if (this.stepperForm) {
      if (
        this.formConfig.hasAction ||
        this.formConfig.hasAction === undefined
      ) {
        if (!PpHelper.isEmpty(valuesObj)) {
          // Start Loader
          this.isLoad = true;
          this.submitFormSubscription = this.apiService
            .request(
              {
                ...this.formConfig.submitApi!,
                config: this.getConfig(this.formConfig.submitApi?.config),
              },
              !PpHelper.isEmpty(this.formConfig.existingData)
                ? { ...this.formConfig.existingData, ...valuesObj }
                : valuesObj,
            )
            .subscribe(
              (res: any) => {
                // Show success message
                this.notificationService.showSuccess(
                  this.formConfig.submitSuccessMessage || 'Saved successfully!',
                );
                // Emit event after form submitted
                this.formSubmitted.emit(true);
                this.getData.emit(res);
                // Enable Submit Button
                this.disableSubmitButton = false;
                // Stop Loader
                this.isLoad = false;
              },
              (error: any) => {
                // Stop Loader
                this.isLoad = false;
                // Enable Submit Button
                if (error.error instanceof ErrorEvent) {
                  console.error('An error occurred:', error.error.message);
                } else {
                  console.error(
                    `Backend returned code ${error.status}, ` +
                      `body was: ${JSON.stringify(error.error)}`,
                  );
                }
                let errorMessage;
                if (error.error.error && error.error.error.message) {
                  errorMessage = error.error.error.message;
                } else {
                  errorMessage =
                    'There were some error(s). Please check your internet connection or try again later.';
                }
                this.errorMsg = errorMessage;
                this.disableSubmitButton = false;
              },
            );
        } else {
          // Emit event after form submitted
          this.formSubmitted.emit(true);
          // Enable Submit Button
          this.disableSubmitButton = false;
        }
      } else {
        this.nextButtonClick.emit(true);
        this.disableSubmitButton = false;
      }
      this.getExistingFormData.emit(
        !PpHelper.isEmpty(this.formConfig.existingData)
          ? { ...this.formConfig.existingData, ...valuesObj }
          : valuesObj,
      );
    } else {
      if (!PpHelper.isEmpty(valuesObj)) {
        // Start Loader
        this.isLoad = true;
        this.submitFormSubscription = this.apiService
          .request(
            {
              ...this.formConfig.submitApi!,
              config: this.getConfig(this.formConfig.submitApi?.config),
            },
            valuesObj,
          )
          .subscribe(
            (res: any) => {
              // Show success message
              this.notificationService.showSuccess(
                this.formConfig.submitSuccessMessage || 'Saved successfully!',
              );
              // Emit event after form submitted
              this.formSubmitted.emit(true);
              this.getData.emit(res);
              // Enable Submit Button
              this.disableSubmitButton = false;
              // Stop Loader
              this.isLoad = false;
            },
            (error: any) => {
              // Stop Loader
              this.isLoad = false;
              // Enable Submit Button
              if (error.error instanceof ErrorEvent) {
                console.error('An error occurred:', error.error.message);
              } else {
                console.error(
                  `Backend returned code ${error.status}, ` +
                    `body was: ${JSON.stringify(error.error)}`,
                );
              }
              let errorMessage;
              if (error.error.error && error.error.error.message) {
                errorMessage = error.error.error.message;
              } else {
                errorMessage =
                  'There were some error(s). Please check your internet connection or try again later.';
              }
              this.errorMsg = errorMessage;
              this.disableSubmitButton = false;
            },
          );
      } else {
        // Emit event after form submitted
        this.formSubmitted.emit(true);
        // Enable Submit Button
        this.disableSubmitButton = false;
      }
    }
  }

  /**
   *
   * @param formFields :CanFormField[]
   * @param values :object
   *
   * Remove 'selectAll' from multicheckbox.
   */
  private removeAllFromMultiCheckBox(
    formFields: PpFormField[],
    values: any,
    form: FormGroup,
  ): void {
    formFields.forEach((formField, index) => {
      if (formField.type === 'group' && formField.formFields?.length) {
        const fg = form.controls[formField.name] as FormGroup;
        // Remove remove 'selectAll' value from multicheckbox in group
        this.removeAllFromMultiCheckBox(
          formField.formFields,
          values[formField.name],
          fg,
        );
      } else if (formField.type === 'array' && formField.formFields?.length) {
        const fa = form.controls[formField.name] as FormArray;
        // Remove remove 'selectAll' value from multicheckbox in formArray
        if (values[formField.name]?.length) {
          values[formField.name].forEach((value: any, index: number) => {
            const fg = fa.controls[index] as FormGroup;
            this.removeAllFromMultiCheckBox(formField.formFields!, value, fg);
          });
        }
      } else {
        if (
          formField.type === 'multicheckbox' &&
          formField?.values?.length &&
          values[formField.name]?.length
        ) {
          if (values[formField.name][0] === 'selectAll') {
            values[formField.name].shift();
          } else {
            values[formField.name] = _.filter(
              values[formField.name],
              (value) => value !== 'selectAll',
            );
          }
        }
      }
    });
  }

  /**
   * Removes select search fields from data before submitting
   */
  private removeSelectSearchTextFields(valuesObj: any): any {
    if (valuesObj && typeof valuesObj === 'object') {
      if (Array.isArray(valuesObj)) {
        return valuesObj.map((obj) => this.removeSelectSearchTextFields(obj));
      }
      const keys = Object.keys(valuesObj) || [];
      for (let key of keys) {
        if (key?.endsWith('_dropdownSearchText')) {
          delete valuesObj[key];
        } else if (typeof valuesObj[key] === 'object') {
          valuesObj[key] = this.removeSelectSearchTextFields(valuesObj[key]);
        }
      }
    }
    return valuesObj;
  }

  /**
   * Set Form Dirty
   */
  private setFormDirty(form: FormGroup): void {
    this.form.markAsDirty();
    form.markAsDirty();
  }

  /**
   *
   * @param hideFields :CanHideFormFields
   * @param value
   *
   * Hide or show fields onn basis on response
   */
  private hideFormFields(hideFields: PpHideFormFields, value: any): void {
    // Check for keys
    if (hideFields && hideFields.keys && hideFields.keys.length) {
      // Loop keys
      hideFields.keys.forEach((key) => {
        // Get Formfield on basis of key
        const formField = this.getRelativeFormField(key);
        // Check for formfield
        if (formField) {
          // Check for operator type
          switch (hideFields.operator) {
            case 'equals':
              formField.isHidden = value === hideFields.value ? true : false;
              break;

            case 'notEquals':
              formField.isHidden = value !== hideFields.value ? true : false;
              break;

            default:
              break;
          }
        }
      });
    }
  }

  removeConfigurationDefaultValueFromChildFields(parentField: PpFormField) {
    if (parentField.formFields?.length) {
      for (let childField of parentField.formFields) {
        if ((childField as any).isDefaultValueSetForConfiguration) {
          delete childField.defaultValue;
        }
        if (childField.type === 'array' || childField.type === 'group') {
          this.removeConfigurationDefaultValueFromChildFields(childField);
        }
      }
    }
  }

  private addDataInArrayChildField(field: PpFormField, data: any) {
    for (let key in data) {
      (field as any)[key] = data[key];
    }
    if (field.type === 'group' && Array.isArray(field.formFields)) {
      for (let nestedField of field.formFields) {
        this.addDataInArrayChildField(nestedField, data);
      }
    }
  }

  private getNestedFormGroupForArrayElement(
    element: PpFormField,
    options: {
      isNew?: boolean;
      defaultValue?: any;
      copyPopulatedData?: boolean;
      populatedDataIndex?: number;
    } = {},
  ): FormGroup {
    let formFields: PpFormField[] = [];
    if (Array.isArray(element.formFields)) {
      formFields = element.formFields.map((field) => {
        const newField = FormsService.deepClone(field);
        if (options.isNew && field.type === 'group') {
          this.removeConfigurationDefaultValueFromChildFields(field);
        }
        return newField;
      });
    }
    if (options.defaultValue) {
      formFields.forEach((field) => {
        if (
          options.defaultValue.hasOwnProperty(field.name) &&
          (field.type === 'group' || field.type === 'array')
        ) {
          field.defaultValue = options.defaultValue[field.name];
        }
      });
    }
    const nestedFormGroup: any = this.configureForm(formFields);
    nestedFormGroup.formFields = formFields;
    formFields.forEach((field) => {
      this.addDataInArrayChildField(field, {
        isArrayChild: true,
        siblings: formFields,
        parentFormGroup: nestedFormGroup,
      });
      if (
        options.copyPopulatedData &&
        element.isDataPopulated &&
        Array.isArray(element.populatedData) &&
        options.populatedDataIndex !== undefined &&
        element.populatedData[options.populatedDataIndex!] &&
        element.populatedData[options.populatedDataIndex!][field.name]
      ) {
        field.isDataPopulated = true;
        field.populatedData =
          element.populatedData[options.populatedDataIndex][field.name];
      }
    });
    return nestedFormGroup;
  }

  /**
   *
   * @param form :FormGroup
   * @param control :CanFormField
   * Add new formgroup to formarray
   */
  public addNewToFormArray(form: FormGroup, control: PpFormField): void {
    // Def's formarray
    const fa = form.controls[control.name] as FormArray;
    // Add new formgroup to form array
    const newNestedGroup = this.getNestedFormGroupForArrayElement(control, {
      isNew: true,
    });
    fa.push(newNestedGroup);
    this.checkValuesForSelectFields((newNestedGroup as any).formFields);
    this.setDefaultForAutocomplete((newNestedGroup as any).formFields);
  }
  /**
   * Set value to autocomplete
   * @param valueItem :CanValueItem
   * @param control :CanFormField
   * @param form :FormGroup
   */
  public setValueToAutocomplete(
    valueItem: PpValueItem,
    control: PpFormField,
    form: FormGroup,
  ): void {
    // Set input field value
    if (control && control.autoComplete)
      control.autoComplete.autocompleteSearchText = valueItem.viewValue;
    // Set control value
    form.controls[control.name].patchValue(valueItem.value);
  }

  /**
   *
   * @param control : CanFormField
   */
  public getAutocompletes(
    control: PpFormField,
    form: FormGroup,
    spinner: any,
  ): void {
    if (control && control.autoComplete)
      control.autoComplete.minCharacters = control.autoComplete.minCharacters
        ? control.autoComplete.minCharacters
        : 1;
    // Initialize for autocomplete values
    control.values = [];
    // Remove subscription
    if (this.autoCompleteSubscription) {
      this.autoCompleteSubscription.unsubscribe();
    }
    // Check for spinner
    if (spinner) {
      // Hide spinner
      spinner._elementRef.nativeElement.style.display = 'none';
    }
    // Check for text
    if (
      control.autoComplete?.autocompleteSearchText &&
      control.autoComplete.autocompleteSearchText.trim() &&
      control.autoComplete.autocompleteSearchText.length >=
        control.autoComplete?.minCharacters!
    ) {
      // Check for spinner
      if (spinner) {
        // Visible spinner
        spinner._elementRef.nativeElement.style.display = 'block';
      }
      // Initialize params
      if (
        !control.autoComplete?.api?.params &&
        control.autoComplete &&
        control.autoComplete.api
      ) {
        control.autoComplete.api.params = new PpHttpParams();
        control.autoComplete.api.params =
          control.autoComplete.api.params.append(
            control.autoComplete?.autocompleteParamKeys![0],
            control.autoComplete.autocompleteSearchText,
          );
      }
      // Get autocomplete list
      this.autoCompleteSubscription = this.apiService
        .request({
          ...control.autoComplete?.api!,
          config: this.getConfig(control.autoComplete?.api?.config),
        })
        .subscribe(
          (res: any) => {
            // Initialize for autocomplete values
            control.values = [];
            const data = PpHelper.getValueWithApiKey(
              res,
              control.autoComplete?.apiDataKey!,
            );
            if (control.autoComplete) control.autoComplete.dataSource = data;
            if (_.isArray(data)) {
              // Add values to particular format
              for (const each of data) {
                control.values.push({
                  value: this.formsService.getValueWithApiKey(
                    each,
                    control.autoComplete?.apiValueKey!,
                  ),
                  viewValue: this.formsService.getValueWithApiKey(
                    each,
                    control.autoComplete?.apiViewValueKey!,
                    control.autoComplete?.viewValueSeparators,
                  ),
                });
              }
            }
            // Check for spinner
            if (spinner) {
              // Hide spinner
              spinner._elementRef.nativeElement.style.display = 'none';
            }
          },
          (err: any) => {
            // Check for spinner
            if (spinner) {
              // Hide spinner
              spinner._elementRef.nativeElement.style.display = 'none';
            }
          },
        );
    }

    // Check for empty
    if (
      !control.autoComplete?.autocompleteSearchText &&
      form.controls[control.name].value
    ) {
      form.controls[control.name].setValue(undefined);
      this.formValueChange(control, form);
    }
  }

  /**
   *
   * @param displayCondition :CanConditionConfig
   * @param form :FormGroup
   * Check whether to display field action or not
   */
  public displayValidate(control: PpFormField, form: FormGroup) {
    // Checking Display Condition Existence
    if (control.displayCondition) {
      const isDisplayed = this.conditionValidationService.validate(
        control.displayCondition,
        form.getRawValue(),
      );

      if (form.get(control.name) instanceof FormControl) {
        if (!isDisplayed) {
          form.get(control.name)?.setValue(control.defaultValue || '');

          if (
            control.pattern ||
            control.required ||
            control.relativeValidation
          ) {
            form.get(control.name)?.setErrors(null);
            form.get(control.name)?.clearValidators();
          }
        } else {
          if (
            control.pattern ||
            control.required ||
            control.relativeValidation
          ) {
            const validators = this.getValidators(control);
            if (validators?.length) {
              form.get(control.name)?.setValidators(validators);
            }
          }
        }
        form.get(control.name)?.updateValueAndValidity();
      }

      return isDisplayed;
    }
    return true;
  }

  /**
   * Back button clicked event
   */
  public back(): void {
    this.backButtonClick.emit(true);
  }

  /**
   *
   * @param value :CanValueItem
   * @param control :CanFormField
   * @param form :FormGroup
   * Check for multiCheckbox value
   */
  public isMultiCheckboxFieldChecked(
    value: PpValueItem,
    control: PpFormField,
    form: FormGroup,
  ): boolean {
    // Check whether value is array
    if (_.isArray(form.controls[control.name].value)) {
      // Check for index
      if (
        form.controls[control.name].value.findIndex(
          (each: any) => each === value.value,
        ) > -1
      ) {
        // If found, return true
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  /**
   *
   * @param value :CanValueItem
   * @param control :CanFormField
   * @param form :FormGroup
   *
   * Event that trigger when multiCheckbox value is changed
   */
  public multiSelectCheckboxChange(
    value: PpValueItem,
    control: PpFormField,
    form: FormGroup,
  ): void {
    // Check whether value is array
    if (_.isArray(form.controls[control.name].value)) {
      // In case 'Select All' checkbox is selected/deselected as current value
      if (value.value === 'selectAll') {
        const indexOfAllCheckBox = (
          form.controls[control.name].value as PpValueItem[]
        ).findIndex((each: any) => each === 'selectAll');
        if (indexOfAllCheckBox > -1) {
          // If 'Select All' is deselected, deselect all values...
          form.controls[control.name].setValue([]);
        } else {
          // If 'Select All' is selected, select all values...
          form.controls[control.name].setValue(
            control.values?.map((el) => el.value),
          );
        }
        return;
      } else {
        /*
          If 'Select All' is not selected as current value,
          Check if there already exists any selected value as 'Select All'
        */
        const indexOfAllCheckBox = form.controls[control.name].value.findIndex(
          (each: any) => each === 'selectAll',
        );

        // If it exists, then deselect it ('Select All').
        if (indexOfAllCheckBox > -1) {
          form.controls[control.name].value.splice(indexOfAllCheckBox, 1);
        }
      }

      // Find index
      const index = form.controls[control.name].value.findIndex(
        (each: any) => each === value.value,
      );
      // If index is found, remove the value
      if (index > -1) {
        form.controls[control.name].value.splice(index, 1);
      } else {
        // Else, add the value
        form.controls[control.name].value.push(value.value);
      }
    } else {
      if (value.value === 'selectAll') {
        /*
          In case the first value is selected as 'Select All',
          then select all...
        */
        form.controls[control.name].setValue(
          control.values?.map((el) => el.value),
        );
        return;
      }
      // Set value as empty array
      form.controls[control.name].setValue([]);
      // Push value to array
      form.controls[control.name].value.push(value.value);
    }
  }

  public patchValueOnAnotherField(control: PpFormField) {
    if (
      control?.fieldsDependentOnCurrentField?.length &&
      control.logicFunction
    ) {
      for (const dependentField of control?.fieldsDependentOnCurrentField) {
        const patchValue = control.logicFunction(
          this.form.get(control.name)?.value,
        );
        this.form.patchValue({
          [dependentField]: patchValue,
        });
      }
    }
  }

  async openPpFormModal(control: PpFormField) {
    if (control?.select?.addButtonConfig?.buttonComponent) {
      let inputData: any = {};

      if (
        control?.select?.addButtonConfig?.inputData &&
        control?.select?.addButtonConfig?.inputData.length
      ) {
        for (let eachData of control?.select?.addButtonConfig?.inputData) {
          inputData[eachData.key] = eachData.value;
        }
      }
      const dialogRef = this.dialog.open(PpModalComponent, {
        disableClose: false,
        width: 'auto',
        maxHeight: '90vh',
        panelClass: 'custom-action-sizing',
        data: {
          component: control?.select?.addButtonConfig?.buttonComponent,
          inputData: inputData,
          header: control?.select?.addButtonConfig?.componentHeader || '',
          dataSource: {},
          reload: false,
        },
      });

      // Check for reload of table after modal is closed
      dialogRef.afterClosed().subscribe(async (dialogResult) => {
        if (dialogResult) {
          control.values = [];

          if (control.select?.api?.params) {
            if (
              control.select?.api?.params.get('page') &&
              control.select.pagination
            ) {
              control.select.api.params =
                control.select?.api?.params.delete('page');
              control.select.pagination.initialOffSet = '1';
            }
          }
          await this.setValuesForSelectFields(control);
          this.form.patchValue({
            [control.name]: dialogResult._id,
          });
        }
      });
    }
  }

  async getAllRelatedValues(
    relatedField: any,
    formField: any,
    isNewCall: boolean,
  ) {
    if (relatedField.select?.pagination) {
      relatedField.select['offSetComplete'] = false;
      if (relatedField.values && relatedField.values.length && isNewCall) {
        relatedField.values = [];
      }
      const offset = parseInt(relatedField.select.pagination.initialOffSet);
      relatedField.select.pagination.initialOffSet = (offset + 1).toString();
      if (
        relatedField.type === 'select' &&
        relatedField.select?.api &&
        relatedField.select.pagination?.pageKey
      ) {
        let emptyVariables = false;
        const api: PpHttpOptions = Object.assign({}, relatedField.select.api);
        if (relatedField.select && api && !api.params) {
          api.params = new PpHttpParams();
        }
        api.params = api.params?.set(
          relatedField.select.pagination?.pageKey,
          relatedField.select.pagination.initialOffSet,
        );
        api.params?.append('limit', relatedField.select.pagination?.limit!);
        const variables = PpHelper.getVariablesFromUrl(api.apiPath);
        // Replace each variable with value
        for (const variable of variables) {
          const value = PpHelper.getValueWithApiKey(
            formField.isArrayChild
              ? formField.parentFormGroup?.getRawValue()
              : this.form.getRawValue(),
            variable,
          );
          // If value is not empty, set value
          if (!PpHelper.isEmpty(value)) {
            api.apiPath = api.apiPath.replace('${' + variable + '}', value);
          } else {
            // Else, set emptyVariables to true and break loop
            emptyVariables = true;
            break;
          }
        }
        // Check for emptyVariables
        if (!emptyVariables) {
          // Check for query params
          const queryParams =
            relatedField.select.relativeApiInfo &&
            relatedField.select.relativeApiInfo.queryParams
              ? relatedField.select.relativeApiInfo.queryParams
              : undefined;
          if (queryParams && queryParams.length) {
            // Check for api params
            if (!api.params) {
              // Init params
              api.params = new PpHttpParams();
            }
            for (const eachParam of queryParams) {
              // Get Param Value
              const value = PpHelper.mapValueWithApiKey(
                formField.isArrayChild
                  ? formField.parentFormGroup?.getRawValue()
                  : this.form.getRawValue(),
                eachParam.value,
              );
              // If value is not empty, set value
              if (!PpHelper.isEmpty(value)) {
                api.params = api.params.set(
                  eachParam.key,
                  this.getRelativeQueryParamValue(value, eachParam),
                );
              } else {
                if (!eachParam.isOptional) {
                  // Else, set emptyVariables to true and break loop
                  emptyVariables = true;
                  break;
                }
              }
            }
          }
          // reset search text
          if (
            api.params &&
            relatedField.select?.searchable &&
            relatedField.select?.searchConfig?.searchKey
          ) {
            api.params = api.params.delete(
              relatedField.select.searchConfig.searchKey,
            );
            (relatedField.searchFormControl as FormControl)?.setValue('', {
              emitViewToModelChange: false, // to avoid search text changed event
              emitEvent: false,
            });
          }
          // If there is no empty variables
          if (!emptyVariables) {
            this.loadSelectData(
              {
                ...relatedField,
                select: {
                  ...relatedField.select,
                  api,
                },
              },
              (res: any) => {
                const data = relatedField.select?.apiDataKey
                  ? PpHelper.getValueWithApiKey(
                      res,
                      relatedField.select?.apiDataKey!,
                    )
                  : res?.data;
                const totalCount = res.paginate?.totalCount;
                if (!relatedField.values) {
                  relatedField.values = [];
                }

                const preSelectedOptions: PpValueItem[] =
                  this.getPreSelectedOptionsForSelect(relatedField);
                const selectedValues =
                  this.formsService.getSelectedValues(relatedField);
                let isSelectedValueLoaded =
                  preSelectedOptions.length === selectedValues.length; // flag to identify whether current selected values' data has been loaded

                relatedField.select.dataSource = data;
                if (data?.length) {
                  for (const each of data) {
                    const { viewValue, secondaryViewValue } =
                      this.getSelectOptionsValues(each, relatedField.select);
                    relatedField.values.push({
                      value: this.formsService.getValueWithApiKey(
                        each,
                        relatedField.select?.apiValueKey!,
                      ),
                      viewValue,
                      secondaryViewValue,
                    });
                  }
                }

                if (preSelectedOptions.length) {
                  // adding preloaded options if not already added
                  preSelectedOptions.forEach((targetItem) => {
                    if (
                      !relatedField.values.some(
                        (row: PpValueItem) => row.value === targetItem.value,
                      )
                    ) {
                      relatedField.values.unshift(targetItem);
                    }
                  });
                }

                // if current selected values are not all present in preloaded data, then checking after merging current API call's result
                if (!isSelectedValueLoaded && selectedValues.length) {
                  isSelectedValueLoaded = selectedValues.every((value) => {
                    return relatedField.values.some(
                      (row: any) => row.value === value,
                    );
                  });
                }

                if (relatedField.select && !data?.length) {
                  // if all options have been loaded
                  relatedField.select.offSetComplete = true;
                  this.selectCurrentValueOptionsLoaded[
                    relatedField.controlId!
                  ] = true;
                  return;
                } else if (!selectedValues.length || isSelectedValueLoaded) {
                  // if all current selected values' data has been loaded
                  this.selectCurrentValueOptionsLoaded[
                    relatedField.controlId!
                  ] = true;
                  return;
                }

                // if there is more data and selected values' data is still not loaded
                this.getAllRelatedValues(relatedField, formField, false);
              },
            );
          }
        }
      }
    }
  }

  private getPreSelectedOptionsForSelect(control: PpFormField): PpValueItem[] {
    const preSelectedOptions: PpValueItem[] = [];
    const selectedValues = this.formsService.getSelectedValues(control);
    if (
      selectedValues.length &&
      control.isDataPopulated &&
      control.populatedData
    ) {
      // preloaded data
      let populatedData: any[] = [];
      if (control.multipleSelect && Array.isArray(control.populatedData)) {
        populatedData = [...control.populatedData];
      } else if (!control.multipleSelect && control.populatedData) {
        populatedData = [control.populatedData];
      }
      if (populatedData.length) {
        // checking if preloaded data has entries for current selected values
        populatedData.forEach((row) => {
          if (typeof row === 'object') {
            const value = this.formsService.getValueWithApiKey(
              row,
              control.select?.apiValueKey!,
            );
            if (selectedValues.includes(value)) {
              const { viewValue, secondaryViewValue } =
                this.getSelectOptionsValues(row, control.select);
              preSelectedOptions.push({
                value,
                viewValue,
                secondaryViewValue,
              });
            }
          } else if (typeof row === 'string') {
            if (selectedValues.includes(row)) {
              preSelectedOptions.push({
                value: row,
                viewValue: row,
              });
            }
          }
        });
      }
    }
    return preSelectedOptions;
  }

  getAllValues(control: PpFormField, resolve: Function) {
    if (control.select?.pagination) {
      const offset = parseInt(control.select.pagination.initialOffSet);

      control.select.pagination.initialOffSet = (offset + 1).toString();
      if (
        control.type === 'select' &&
        control.select?.api &&
        control.select.pagination?.pageKey
      ) {
        // Get variables from url
        const variables = PpHelper.getVariablesFromUrl(
          control.select.api.apiPath,
        );
        // Replace each variable with value
        for (const variable of variables) {
          const value = PpHelper.getValueWithApiKey(this.form, variable);
          // If value is not empty, set value
          if (!PpHelper.isEmpty(value)) {
            control.select.api.apiPath = control.select.api.apiPath.replace(
              '${' + variable + '}',
              value,
            );
          }
        }
        control.select.api.params = control.select.api.params?.set(
          control.select.pagination?.pageKey,
          control.select.pagination.initialOffSet,
        );
        control.select.api.params?.append(
          'limit',
          control.select.pagination?.limit!,
        );
        const queryParams =
          control.select.relativeApiInfo &&
          control.select.relativeApiInfo.queryParams
            ? control.select.relativeApiInfo.queryParams
            : undefined;
        if (queryParams && queryParams.length) {
          // Check for api params
          if (!control.select.api.params) {
            // Init params
            control.select.api.params = new PpHttpParams();
          }
          for (const eachParam of queryParams) {
            // Get Param Value
            const value = PpHelper.mapValueWithApiKey(
              this.form,
              eachParam.value,
            );
            // If value is not empty, set value
            if (!PpHelper.isEmpty(value)) {
              control.select.api.params = control.select.api.params.set(
                eachParam.key,
                this.getRelativeQueryParamValue(value, eachParam),
              );
            }
          }
        }
        this.loadSelectData(control, (res: any) => {
          const data = control.select?.apiDataKey
            ? PpHelper.getValueWithApiKey(res, control.select?.apiDataKey!)
            : res?.data;
          if (_.isArray(data) && control.select) {
            // Assign dataSource
            control.select.dataSource = data;
          }
          const totalCount = res.paginate?.totalCount || 0;
          if (!control.values) {
            control.values = [];
          }

          const preSelectedOptions: PpValueItem[] =
            this.getPreSelectedOptionsForSelect(control);
          const selectedValues = this.formsService.getSelectedValues(control);
          let isSelectedValueLoaded =
            selectedValues.length === preSelectedOptions.length; // flag to identify whether current selected values' data has been loaded

          if (data?.length) {
            for (const each of data) {
              const { viewValue, secondaryViewValue } =
                this.getSelectOptionsValues(each, control.select);
              control.values.push({
                value: this.formsService.getValueWithApiKey(
                  each,
                  control.select?.apiValueKey!,
                ),
                viewValue,
                secondaryViewValue,
              });
            }
          }

          if (preSelectedOptions.length) {
            // adding preloaded options if not already added
            preSelectedOptions.forEach((targetItem) => {
              if (
                !control.values?.some(
                  (row: PpValueItem) => row.value === targetItem.value,
                )
              ) {
                control.values?.unshift(targetItem);
              }
            });
          }

          // if current selected values are not all present in preloaded data, then checking after merging current API call's result
          if (!isSelectedValueLoaded && selectedValues.length) {
            isSelectedValueLoaded = selectedValues.every((targetValue) => {
              return control.values?.some((row) => row.value === targetValue);
            });
          }

          if (control.select && !data?.length) {
            // if all options have been loaded
            control.select.offSetComplete = true;
            this.selectCurrentValueOptionsLoaded[control.controlId!] = true;
            resolve();
            return;
          } else if (!selectedValues.length || isSelectedValueLoaded) {
            // if all current selected values' data has been loaded
            this.selectCurrentValueOptionsLoaded[control.controlId!] = true;
            resolve();
            return;
          } else {
            // if there is more data and current selected values' data is still not loaded
            this.getAllValues(control, resolve);
          }
        });
      } else {
        resolve();
      }
    } else {
      resolve();
    }
  }

  /**
   *
   * @param control
   * @param callback
   * loads options for select control
   */
  private loadSelectData(control: PpFormField, callback: Function): void {
    if (control.type === 'select' && control.select?.api) {
      if (this.selectSubscription[control.controlId!]) {
        // if any other call in progress, abort it.
        this.selectSubscription[control.controlId!].unsubscribe();
      }
      this.selectApiLoading[control.controlId!] = true;
      this.selectApiError[control.controlId!] = '';
      this.selectSubscription[control.controlId!] = this.apiService
        .request({
          ...control.select.api,
          config: this.getConfig(control.select.api?.config),
        })
        .subscribe(
          (res) => {
            this.selectApiLoading[control.controlId!] = false;
            callback && callback(res);
          },
          (error: any) => {
            this.selectApiLoading[control.controlId!] = false;
            let errorMessage = error?.error?.error?.message;
            if (!errorMessage || typeof errorMessage !== 'string') {
              if (error?.message && typeof error?.message === 'string') {
                errorMessage = error.message;
              } else {
                errorMessage = 'API failed. Please check with Admin tech team.';
              }
            }
            if (error?.status === 403) {
              errorMessage = 'PERMISSION_ERROR';
            }
            this.selectApiError[control.controlId!] = errorMessage;
          },
        );
    }
  }

  public isPermissionError(control: PpFormField) {
    return this.selectApiError[control.controlId!] === 'PERMISSION_ERROR';
  }

  public getPermissionNameToDisplay(control: PpFormField) {
    if (control.permissionNames?.length) {
      return [...new Set(control.permissionNames.filter(Boolean))].join(', ');
    }
    return '';
  }

  public copyPermissionName(control: PpFormField) {
    navigator.clipboard?.writeText(this.getPermissionNameToDisplay(control));
    this.notificationService.showSuccess('Permission copied!');
  }

  /**
   *
   * @param control
   * @returns whether current selected values' options have been loaded or not
   */
  isSelectCurrentValueOptionsLoaded(control: PpFormField): boolean {
    return (
      !this.formsService.getSelectedValues(control).length ||
      this.selectCurrentValueOptionsLoaded[control.controlId!]
    );
  }

  isOptionDisabled(
    value: string,
    formField: PpFormField,
    form: FormGroup,
  ): boolean {
    return formField?.multipleSelect &&
      formField?.selectionLimit?.value &&
      form.controls[formField.name]?.value
      ? form.controls[formField.name].value?.length >=
          formField.selectionLimit.value &&
          !form.controls[formField.name].value?.find((el: any) => el == value)
      : false;
  }

  onSelectSearchTextChange(searchText: string, control: PpFormField) {
    if (control.type === 'select' && control.select?.searchable) {
      searchText = searchText?.trim();
      const pattern = control.select.searchConfig?.pattern?.value;
      const patternErrorMessage =
        control.select.searchConfig?.pattern?.errorMessage;
      if (pattern) {
        const patternRegex = new RegExp(pattern);
        if (searchText && !patternRegex.test(searchText)) {
          if (patternErrorMessage) {
            this.notificationService.showError(patternErrorMessage);
          }
          return; // Prevent API call if pattern doesn't match
        }
      }
      if (control.select?.api) {
        const api: PpHttpOptions = Object.assign({}, control.select.api);
        if (!api.params) {
          // Init params
          api.params = new PpHttpParams();
        }
        // Get variables from url
        const variables = PpHelper.getVariablesFromUrl(api.apiPath);
        // Replace each variable with value
        for (const variable of variables) {
          const value = PpHelper.getValueWithApiKey(
            control.isArrayChild
              ? control.parentFormGroup?.getRawValue()
              : this.form.getRawValue(),
            variable,
          );
          // If value is not empty, set value
          if (!PpHelper.isEmpty(value)) {
            api.apiPath = api.apiPath.replace('${' + variable + '}', value);
          }
        }
        if (control.select.pagination) {
          control.select.pagination.initialOffSet = '1';
          control.select.offSetComplete = false;
          if (control.select.pagination?.pageKey) {
            api.params = api.params?.set(
              control.select.pagination?.pageKey,
              control.select.pagination.initialOffSet,
            );
          }
          if (control.select.pagination.limit) {
            api.params = api.params?.set(
              'limit',
              control.select.pagination?.limit!,
            );
          }
        }
        const queryParams =
          control.select.relativeApiInfo &&
          control.select.relativeApiInfo.queryParams
            ? control.select.relativeApiInfo.queryParams
            : undefined;
        if (queryParams && queryParams.length) {
          for (const eachParam of queryParams) {
            // Get Param Value
            const value = PpHelper.mapValueWithApiKey(
              control.isArrayChild
                ? control.parentFormGroup?.getRawValue()
                : this.form.getRawValue(),
              eachParam.value,
            );
            // If value is not empty, set value
            if (!PpHelper.isEmpty(value)) {
              api.params = api.params.set(
                eachParam.key,
                this.getRelativeQueryParamValue(value, eachParam),
              );
            }
          }
        }
        if (control.select.searchConfig?.searchKey) {
          if (searchText) {
            // if search text, then add search param
            api.params = api.params.set(
              control.select.searchConfig.searchKey,
              searchText,
            );
          } else {
            // remove search param
            api.params = api.params.delete(
              control.select.searchConfig.searchKey,
            );
          }
        }

        this.loadSelectData(
          {
            ...control,
            select: {
              ...control.select,
              api,
            },
          },
          (res: any) => {
            let preSelectedOptions: PpValueItem[] = [];
            if (control.values?.length) {
              const selectedValues =
                this.formsService.getSelectedValues(control);
              // filter and keep current selected options
              preSelectedOptions = control.values.filter(
                (row) => selectedValues.indexOf(row.value) !== -1,
              );
            }
            control.values = [...preSelectedOptions];
            const data = control.select?.apiDataKey
              ? PpHelper.getValueWithApiKey(res, control.select?.apiDataKey!)
              : res?.data;
            const totalCount = res.paginate?.totalCount || 0;
            const prevDataSource = Array.isArray(control.select!.dataSource)
              ? control.select!.dataSource
              : [];
            const prevSelectedData = prevDataSource.filter((each: any) => {
              const itemValue = this.formsService.getValueWithApiKey(
                each,
                control.select?.apiValueKey!,
              );
              return preSelectedOptions.some(
                (option) => option.value === itemValue,
              );
            });
            control.select!.dataSource = [...prevSelectedData];
            if (data?.length) {
              for (const each of data) {
                const itemValue = this.formsService.getValueWithApiKey(
                  each,
                  control.select?.apiValueKey!,
                );
                if (
                  !preSelectedOptions.length ||
                  !preSelectedOptions.some(
                    (option) => option.value === itemValue,
                  )
                ) {
                  control.select!.dataSource.push(each);
                  const { viewValue, secondaryViewValue } =
                    this.getSelectOptionsValues(each, control.select);
                  control.values?.push({
                    value: itemValue,
                    viewValue,
                    secondaryViewValue,
                  });
                }
              }
            }
          },
        );
      } else {
        // local search
        const allValues = this.selectAllValuesBackup[control.controlId!] || [];
        if (!searchText) {
          control.values = allValues;
        } else {
          control.values = allValues.filter(
            (row) =>
              row.viewValue?.toLowerCase().indexOf(searchText.toLowerCase()) !==
              -1,
          );
        }
      }
    }
  }

  onScroll(control: PpFormField) {
    if (
      control.select?.pagination &&
      !this.selectApiLoading[control.controlId!] &&
      this.isSelectCurrentValueOptionsLoaded(control)
    ) {
      const offset = parseInt(control.select.pagination.initialOffSet);
      control.select.pagination.initialOffSet = (offset + 1).toString();
      if (
        control.type === 'select' &&
        control.select?.api &&
        control.select.pagination?.pageKey
      ) {
        const api: PpHttpOptions = Object.assign({}, control.select.api);
        // Get variables from url
        const variables = PpHelper.getVariablesFromUrl(api.apiPath);
        // Replace each variable with value
        for (const variable of variables) {
          const value = PpHelper.getValueWithApiKey(
            control.isArrayChild
              ? control.parentFormGroup?.getRawValue()
              : this.form.getRawValue(),
            variable,
          );
          // If value is not empty, set value
          if (!PpHelper.isEmpty(value)) {
            api.apiPath = api.apiPath.replace('${' + variable + '}', value);
          }
        }
        if (!api.params) {
          // Init params
          api.params = new PpHttpParams();
        }
        api.params = api.params.set(
          control.select.pagination?.pageKey,
          control.select.pagination.initialOffSet,
        );
        if (control.select.pagination.limit) {
          api.params = api.params.set(
            'limit',
            control.select.pagination?.limit!,
          );
        }
        const queryParams =
          control.select.relativeApiInfo &&
          control.select.relativeApiInfo.queryParams
            ? control.select.relativeApiInfo.queryParams
            : undefined;
        if (queryParams && queryParams.length) {
          // Check for api params
          for (const eachParam of queryParams) {
            // Get Param Value
            const value = PpHelper.mapValueWithApiKey(
              control.isArrayChild
                ? control.parentFormGroup?.getRawValue()
                : this.form.getRawValue(),
              eachParam.value,
            );
            // If value is not empty, set value
            if (!PpHelper.isEmpty(value)) {
              api.params = api.params.set(
                eachParam.key,
                this.getRelativeQueryParamValue(value, eachParam),
              );
            }
          }
        }
        // add search text param
        if (
          control.select.searchable &&
          control.select.searchConfig?.searchKey
        ) {
          const searchText = control.searchFormControl?.value || '';
          if (searchText) {
            api.params = api.params.set(
              control.select.searchConfig.searchKey,
              searchText,
            );
          }
        }

        this.loadSelectData(
          {
            ...control,
            select: {
              ...control.select,
              api,
            },
          },
          (res: any) => {
            const data = control.select?.apiDataKey
              ? PpHelper.getValueWithApiKey(res, control.select?.apiDataKey!)
              : res?.data;
            const totalCount = res.paginate?.totalCount || 0;
            if (control.values?.length && data?.length) {
              const selectedValues =
                this.formsService.getSelectedValues(control);
              const allControlValues = control.values.map((row) => row.value);
              control.select!.dataSource = (
                control.select!.dataSource ?? []
              )?.concat(data);
              for (const each of data) {
                const itemValue = this.formsService.getValueWithApiKey(
                  each,
                  control.select?.apiValueKey!,
                );
                // to avoid duplicate entry for selected value
                if (
                  !selectedValues.includes(itemValue) ||
                  !allControlValues.includes(itemValue)
                ) {
                  const { viewValue, secondaryViewValue } =
                    this.getSelectOptionsValues(each, control.select);
                  control.values.push({
                    value: itemValue,
                    viewValue,
                    secondaryViewValue,
                  });
                }
              }
            }

            const controlValuesLength = control.values?.length || 0;
            if (control.select && !data?.length) {
              control.select.offSetComplete = true;
            }
          },
        );
      }
    }
  }

  private getConfig(config?: PpHttpConfig): PpHttpConfig {
    const localConfigBaseUrl = config?.baseUrl;

    return {
      ...this.config,
      ...(localConfigBaseUrl && { baseUrl: localConfigBaseUrl }),
    };
  }

  ngOnDestroy(): void {
    for (let controlId in this.selectSubscription) {
      if (this.selectSubscription[controlId]) {
        this.selectSubscription[controlId].unsubscribe();
      }
    }
  }

  openPreview(previewUrl: string) {
    const dialogRef = this.dialog.open(PreviewDialogComponent, {
      data: { previewUrl: previewUrl },
    });
  }

  private getRelativeQueryParamValue(
    value: any,
    eachParam: PpParamInfo,
  ): string {
    if (eachParam.regex) {
      return JSON.stringify({ ilike: '%' + value + '%' });
    } else if (eachParam.date === 'isStartOfDay') {
      return moment(value).startOf('day').toISOString();
    } else if (eachParam.date === 'isEndOfDay') {
      return moment(value).endOf('day').toISOString();
    }
    return value;
  }

  private getSelectOptionsValues(formData: any, select: any): any {
    const viewValue = select?.apiViewValueKey!
      ? this.formsService.getValueWithApiKey(
          formData,
          select?.apiViewValueKey!,
          select?.viewValueSeparators,
        )
      : null;
    const secondaryViewValue = select?.viewValueKey
      ? this.formsService.getValueWithApiKeySecondary(
          formData,
          select?.viewValueKey,
        )
      : null;

    return { viewValue, secondaryViewValue };
  }
}
